import { DateTime } from 'luxon'
import { isNotDefined } from '@terros/common'

export type EntitlementExpiry = {
  label: string
  tooltip: string
}

const TOOLTIP_FORMAT = 'LLL d, yyyy, h:mm a ZZZZ'

/**
 * Exact expiry wording for an entitlement chip. `expiresAt` is in epoch seconds; an entitlement
 * without one never expires, so it gets no expiry wording at all.
 *
 * Days are counted in calendar days in the viewer's zone (the zone of `now`), not as elapsed
 * 24-hour periods: a 90-day grant reads "90 days" for the rest of the day it was granted, and
 * one that runs out at 1 AM tomorrow reads "tomorrow" rather than "today".
 */
export function getEntitlementExpiry(
  expiresAt: number | undefined,
  now: DateTime = DateTime.local()
): EntitlementExpiry | undefined {
  if (isNotDefined(expiresAt)) return undefined
  const expiry = DateTime.fromSeconds(expiresAt, { zone: now.zone, locale: now.locale ?? undefined })
  const formatted = expiry.toFormat(TOOLTIP_FORMAT)
  if (expiry <= now) return { label: 'Expired', tooltip: `Expired ${formatted}` }
  return { label: toDaysLabel(expiry, now), tooltip: `Expires ${formatted}` }
}

function toDaysLabel(expiry: DateTime, now: DateTime): string {
  const days = expiry.startOf('day').diff(now.startOf('day'), 'days').days
  if (days < 1) return 'Expires today'
  if (days === 1) return 'Expires tomorrow'
  return `Expires in ${days} days`
}
